import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import DataFlowIcon from '@atlaskit/icon/core/data-flow';
import DataStringIcon from '@atlaskit/icon/core/data-string';
import DatabaseIcon from '@atlaskit/icon/core/database';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import ProjectionScreenIcon from '@atlaskit/icon/core/projection-screen';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { RemixHiddenOptionKind } from '../../../types';

import messages from './messages';
import type { RemixOption, RemixSubType, RemixSwitcherInfo } from './types';

/**
 * All available remix options with their sub-types (variants).
 *
 * Sub-type values match the backend `dynamicUiSubtype` parameter
 * used in the remix-content minion (see edit-with-rovo-bar).
 */
const defaultInfographicSubTypes: RemixSubType[] = [
	{
		value: 'corporate-modern',
		displayName: 'modern style infographic',
		labelMessage: messages.subTypeModernStyle,
		testId: 'remix-sub-type-corporate-modern',
	},
	{
		value: 'corporate-doodle',
		displayName: 'sketch style infographic',
		labelMessage: messages.subTypeSketchStyle,
		testId: 'remix-sub-type-corporate-doodle',
	},
];

const newInfographicSubTypes: RemixSubType[] = [
	{
		value: 'charlie',
		displayName: 'bold style infographic',
		labelMessage: messages.subTypeBoldStyle,
		testId: 'remix-sub-type-charlie',
	},
	{
		value: 'minimalist',
		displayName: 'minimalist style infographic',
		labelMessage: messages.subTypeMinimalistStyle,
		testId: 'remix-sub-type-minimalist',
	},
	{
		value: 'corporate-doodle',
		displayName: 'sketch style infographic',
		labelMessage: messages.subTypeSketchStyle,
		testId: 'remix-sub-type-corporate-doodle',
	},
];

export function getInfographicSubTypes(): RemixSubType[] {
	return fg('cc-maui-phase-2') ? newInfographicSubTypes : defaultInfographicSubTypes;
}

const defaultVisualAidSubTypes: RemixSubType[] = [
	{
		value: 'flip-card',
		displayName: 'flip card',
		labelMessage: messages.subTypeFlipCard,
		testId: 'remix-sub-type-flip-card',
	},
	{
		value: 'quadrant',
		displayName: 'quadrant',
		labelMessage: messages.subTypeQuadrant,
		testId: 'remix-sub-type-quadrant',
	},
	{
		value: 'tree-map',
		displayName: 'tree map',
		labelMessage: messages.subTypeTreeMap,
		testId: 'remix-sub-type-tree-map',
	},
	{
		value: 'horizontal-timeline',
		displayName: 'timeline',
		labelMessage: messages.subTypeTimeline,
		testId: 'remix-sub-type-horizontal-timeline',
	},
	{
		value: 'map',
		displayName: 'map',
		labelMessage: messages.subTypeGeographicalMap,
		testId: 'remix-sub-type-map',
	},
	{
		value: 'org-chart',
		displayName: 'org chart',
		labelMessage: messages.subTypeOrgChart,
		testId: 'remix-sub-type-org-chart',
	},
];

const imageSubType: RemixSubType = {
	value: 'image',
	displayName: 'image',
	labelMessage: messages.subTypeImage,
	testId: 'remix-sub-type-image',
	emitType: 'image',
};

export function getVisualAidSubTypes(): RemixSubType[] {
	return fg('cc-maui-phase-3')
		? [...defaultVisualAidSubTypes, imageSubType]
		: defaultVisualAidSubTypes;
}

export const REMIX_OPTIONS: RemixOption[] = [
	{
		type: 'chart',
		displayName: 'chart',
		icon: ChartBarIcon,
		labelMessage: messages.remixOptionChart,
		iconAltMessage: messages.chartIconAlt,
		tooltipMessage: messages.chartTooltip,
		testId: 'remix-overlay-option-chart',
		subTypes: [
			{
				value: 'line-chart',
				displayName: 'line chart',
				labelMessage: messages.subTypeLineChart,
				testId: 'remix-sub-type-line-chart',
			},
			{
				value: 'area-chart',
				displayName: 'area chart',
				labelMessage: messages.subTypeAreaChart,
				testId: 'remix-sub-type-area-chart',
			},
			{
				value: 'bar-chart',
				displayName: 'bar chart',
				labelMessage: messages.subTypeBarChart,
				testId: 'remix-sub-type-bar-chart',
			},
			{
				value: 'funnel-chart',
				displayName: 'funnel chart',
				labelMessage: messages.subTypeFunnelChart,
				testId: 'remix-sub-type-funnel-chart',
			},
			{
				value: 'pie-chart',
				displayName: 'pie chart',
				labelMessage: messages.subTypePieChart,
				testId: 'remix-sub-type-pie-chart',
			},
			{
				value: 'gantt-chart',
				displayName: 'Gantt chart',
				labelMessage: messages.subTypeGanttChart,
				testId: 'remix-sub-type-gantt-chart',
			},
		],
	},
	{
		type: 'diagram',
		displayName: 'diagram',
		icon: DataFlowIcon,
		labelMessage: messages.remixOptionDiagram,
		iconAltMessage: messages.diagramIconAlt,
		tooltipMessage: messages.diagramTooltip,
		testId: 'remix-overlay-option-diagram',
		subTypes: [
			{
				value: 'mindmap',
				displayName: 'mind map',
				labelMessage: messages.subTypeMindMap,
				testId: 'remix-sub-type-mindmap',
			},
			{
				value: 'flowchart',
				displayName: 'flowchart',
				labelMessage: messages.subTypeFlowChart,
				testId: 'remix-sub-type-flowchart',
			},
		],
	},
	{
		type: 'infographic',
		displayName: 'infographic',
		icon: DataStringIcon,
		labelMessage: messages.remixOptionInfographic,
		iconAltMessage: messages.infographicIconAlt,
		tooltipMessage: messages.infographicTooltip,
		testId: 'remix-overlay-option-infographic',
		subTypes: defaultInfographicSubTypes,
	},
	{
		type: 'visual-aid',
		displayName: 'visualization',
		icon: EyeOpenIcon,
		labelMessage: messages.remixOptionVisualization,
		iconAltMessage: messages.visualizationIconAlt,
		tooltipMessage: messages.visualizationTooltip,
		testId: 'remix-overlay-option-visual-aid',
		subTypes: defaultVisualAidSubTypes,
	},
];

/**
 * Page-level remix options shown from the ObjectSidebar entry point.
 * Uses the entire page ADF instead of selected content.
 *
 * Options with empty subTypes render as flat clickable rows;
 * options with subTypes render as expandable accordions.
 */
export const PAGE_REMIX_OPTIONS: RemixOption[] = [
	{
		type: 'slides',
		displayName: 'slides',
		icon: ProjectionScreenIcon,
		labelMessage: messages.remixOptionSlides,
		iconAltMessage: messages.slidesIconAlt,
		tooltipMessage: messages.slidesTooltip,
		testId: 'remix-overlay-option-slides',
		subTypes: [],
		iconTileAppearance: 'green',
	},
	{
		type: 'whiteboard',
		displayName: 'whiteboard',
		icon: WhiteboardIcon,
		labelMessage: messages.remixOptionWhiteboard,
		iconAltMessage: messages.whiteboardIconAlt,
		tooltipMessage: messages.whiteboardTooltip,
		testId: 'remix-overlay-option-whiteboard',
		subTypes: [],
		iconTileAppearance: 'teal',
	},
	{
		type: 'database',
		displayName: 'database',
		icon: DatabaseIcon,
		labelMessage: messages.remixOptionDatabase,
		iconAltMessage: messages.databaseIconAlt,
		tooltipMessage: messages.databaseTooltip,
		testId: 'remix-overlay-option-database',
		subTypes: [],
		iconTileAppearance: 'purple',
	},
	{
		type: 'infographic',
		displayName: 'infographic',
		icon: DataStringIcon,
		labelMessage: messages.remixOptionInfographic,
		iconAltMessage: messages.infographicIconAlt,
		tooltipMessage: messages.infographicTooltip,
		testId: 'remix-overlay-option-infographic',
		subTypes: defaultInfographicSubTypes,
	},
];

function applyGatedSubTypes(remixChoices: readonly RemixOption[]): RemixOption[] {
	const infographicSubTypes = getInfographicSubTypes();
	const visualAidSubTypes = getVisualAidSubTypes();

	return remixChoices.map((opt) => {
		if (opt.type === 'infographic') {
			return { ...opt, subTypes: infographicSubTypes };
		}
		if (opt.type === 'visual-aid') {
			return { ...opt, subTypes: visualAidSubTypes };
		}
		return opt;
	});
}

export type RemixPreset = 'selection' | 'page';

type RemixMenuOptionsSource =
	| { kind: 'preset'; preset: RemixPreset }
	| { kind: 'custom'; choices: readonly RemixOption[] };

interface GetRemixMenuOptionsParams {
	source?: RemixMenuOptionsSource;
	excludedRemixTypes?: readonly RemixHiddenOptionKind[];
}

/**
 * Keeps shared Remix categories, labels, icons, and feature-gated subtypes consistent across
 * presentations
 */
export function getRemixMenuOptions({
	source = { kind: 'preset', preset: 'selection' },
	excludedRemixTypes,
}: GetRemixMenuOptionsParams = {}): RemixOption[] {
	let sourceChoices: readonly RemixOption[];

	switch (source.kind) {
		case 'preset':
			sourceChoices = source.preset === 'page' ? PAGE_REMIX_OPTIONS : REMIX_OPTIONS;
			break;
		case 'custom':
			sourceChoices = source.choices;
			break;
	}

	return getVisibleRemixOptions(applyGatedSubTypes(sourceChoices), excludedRemixTypes);
}

/** Render-time lookup reflects current feature gates */
function getAllOptions(): RemixOption[] {
	return getRemixMenuOptions({
		source: {
			kind: 'custom',
			choices: [...REMIX_OPTIONS, ...PAGE_REMIX_OPTIONS],
		},
	});
}

export function getVisibleRemixOptions(
	remixChoices: RemixOption[],
	excludedRemixTypes?: readonly RemixHiddenOptionKind[],
): RemixOption[] {
	if (!excludedRemixTypes?.length) {
		return remixChoices;
	}

	const excludedRemixTypeSet = new Set<RemixHiddenOptionKind>(excludedRemixTypes);
	return remixChoices.filter(
		(choice) => choice.type === 'custom' || !excludedRemixTypeSet.has(choice.type),
	);
}

/**
 * Returns the human-readable display name for a type value.
 * Falls back to replacing hyphens with spaces if the value is not found.
 *
 * Used by consumers (e.g. Confluence) to construct natural-language prompts.
 */
export function getTypeDisplayName(typeValue: string): string {
	const match = getAllOptions().find((option) => option.type === typeValue);
	return match?.displayName ?? typeValue.replace(/-/g, ' ');
}

/**
 * Returns the human-readable display name for a sub-type value.
 * Falls back to replacing hyphens with spaces if the value is not found.
 *
 * Used by consumers (e.g. Confluence) to construct natural-language prompts.
 */
export function getSubTypeDisplayName(subTypeValue: string): string {
	for (const option of getAllOptions()) {
		const match = option.subTypes.find((st) => st.value === subTypeValue);
		if (match) {
			return match.displayName;
		}
	}
	return subTypeValue.replace(/-/g, ' ');
}

/**
 * Returns the context needed to render a remix type switcher dropdown
 * in the floating toolbar.
 *
 * Given a remix node's current `dynamicUiType` and `dynamicUiSubtype`,
 * resolves the parent category label, current sub-type label, and the
 * full list of sibling sub-types within that category.
 *
 * Returns `null` when the type is not found or has no sub-types (e.g.
 * 'custom', 'slides', 'whiteboard', 'database').
 *
 * Handles `emitType` overrides: when a sub-type's `emitType` differs
 * from its parent category, the function scans all categories to find
 * the correct match by sub-type value or emitType.
 */
export function getRemixSwitcherInfo(
	dynamicUiType: string,
	dynamicUiSubtype?: string,
): RemixSwitcherInfo | null {
	const allOptions = getAllOptions();

	// First, try to find the category by matching the dynamicUiType directly.
	let matchedOption = allOptions.find((opt) => opt.type === dynamicUiType);

	// If the direct match doesn't contain the requested sub-type (e.g. the
	// node's emitType points to a different category than where the sub-type
	// actually lives), or if no category matched at all, fall back to scanning
	// all categories for a sub-type whose value or emitType matches.
	if (dynamicUiSubtype) {
		const subtypeInMatch = matchedOption?.subTypes.some((st) => st.value === dynamicUiSubtype);
		if (!matchedOption || !subtypeInMatch) {
			const fallback = allOptions.find((opt) =>
				opt.subTypes.some((st) => st.value === dynamicUiSubtype || st.emitType === dynamicUiType),
			);
			if (fallback) {
				matchedOption = fallback;
			}
		}
	} else if (!matchedOption) {
		// No subtype provided and no direct category match — the dynamicUiType
		// may be an emitType override (e.g. 'image' which is a sub-type in
		// 'visual-aid' with emitType: 'image'). Scan all categories for a
		// sub-type whose emitType matches.
		const fallback = allOptions.find((opt) =>
			opt.subTypes.some((st) => st.emitType === dynamicUiType),
		);
		if (fallback) {
			matchedOption = fallback;
		}
	}

	// No matching category, or category has no sub-types (e.g. slides, whiteboard)
	if (!matchedOption || matchedOption.subTypes.length === 0) {
		return null;
	}

	const groupLabel = (matchedOption.labelMessage.defaultMessage as string) ?? '';
	const groupLabelMessage = matchedOption.labelMessage;

	// Find the current sub-type within the matched category.
	// When dynamicUiSubtype is provided, match by value directly.
	// When only dynamicUiType is provided (no subtype), try to find a sub-type
	// whose emitType matches dynamicUiType (e.g. emitType: 'image' → the 'image' sub-type).
	let currentSubType = dynamicUiSubtype
		? matchedOption.subTypes.find((st) => st.value === dynamicUiSubtype)
		: matchedOption.subTypes.find((st) => st.emitType === dynamicUiType);

	// If a subtype was provided but not found in this category, return null
	// to avoid showing a broken dropdown.
	if (dynamicUiSubtype && !currentSubType) {
		return null;
	}

	// Variants with `emitType` are only selectable from the remix-overlay.
	// When the active subtype has an emitType override, hide the switcher
	// entirely so the user cannot switch away from it.
	if (currentSubType?.emitType) {
		return null;
	}

	// Filter out emitType variants from the switcher — they should only
	// appear in the remix-overlay, not in the editor toolbar dropdown.
	const filteredSubTypes = matchedOption.subTypes.filter((st) => !st.emitType);
	if (filteredSubTypes.length === 0) {
		return null;
	}

	// Fall back to category-level label if no sub-type is set
	// (e.g. "Pick for me" was used).
	const currentLabel = currentSubType
		? ((currentSubType.labelMessage.defaultMessage as string) ?? '')
		: groupLabel;
	const currentLabelMessage = currentSubType ? currentSubType.labelMessage : groupLabelMessage;

	return {
		groupLabel,
		groupLabelMessage,
		currentLabel,
		currentLabelMessage,
		siblingSubTypes: filteredSubTypes,
	};
}
